import { useEffect, useMemo } from 'react';

import { USDC_DECIMALS } from '@/lib/api/speed/constants';

import { useTradingStore } from '../store';
import {
    leverageFromTrade,
    priceAmount,
    tokenAmount,
} from '../utils/tradeFormat';

import { useActiveUserTrades } from './useActiveUserTrades';

/**
 * Mirrors the server's active-trades list into the store's per-coin position
 * map.
 *
 * The execution hook only knows about markets THIS client opened or hydrated.
 * Everything else a user holds — opened in another tab, opened before a reload,
 * or opened on a market they have since navigated away from — reaches the UI
 * only through this list. Before multiple positions existed the gap did not show:
 * the single-trade snapshot in localStorage covered the one position that could
 * exist. It cannot cover five.
 *
 * The list is authoritative for WHICH markets are open. It is deliberately not
 * authoritative for the live figures: `entryPrice` here is the backend's record,
 * while the exchange's size-weighted average and its PnL arrive from the position
 * poll and are merged per coin by `_syncLivePosition`.
 */
export function useSyncActiveTradesToStore(): void {
    const { data: activeTrades } = useActiveUserTrades();
    const runtimeMode = useTradingStore((s) => s.runtimeMode);

    // Map to the store's shape here rather than in the store, so the store stays
    // free of API representation details (base-unit strings, `isLong`, the
    // absence of an explicit leverage field).
    const slots = useMemo(
        () =>
            (activeTrades ?? []).map((trade) => {
                const margin = tokenAmount(trade.margin, USDC_DECIMALS);
                // A Resting row has no entry price (nothing filled); its limit
                // price is the only price it has, and the card shows it as such.
                const entry = priceAmount(trade.entryPrice);
                const createdAt = Date.parse(String(trade.createdAt));
                return {
                    coin: trade.pairBase,
                    tradeHash: trade.tradeHash,
                    openTxHash: trade.openTxHash ?? null,
                    positionType: (trade.isLong ? 'LONG' : 'SHORT') as
                        'LONG' | 'SHORT',
                    positionAmount: margin,
                    entryPrice: entry,
                    // the row carries the leverage the user chose (null on legacy
                    // rows, where it is re-derived as qty x entry / margin). A
                    // resting order has no fill to derive it from, so the persisted
                    // value is the only one it has.
                    leverage:
                        trade.leverage ??
                        leverageFromTrade(trade, margin, entry),
                    limitPrice: trade.limitPrice ?? null,
                    openedAt: Number.isNaN(createdAt) ? null : createdAt,
                    // 'Pending' means accepted but not yet filled, 'Resting' a
                    // GTC limit entry on the book — the store's own status
                    // vocabulary is lower-case.
                    status: (trade.status === 'Pending'
                        ? 'pending'
                        : trade.status === 'Resting'
                          ? 'resting'
                          : 'open') as 'pending' | 'open' | 'resting',
                };
            }),
        [activeTrades],
    );

    useEffect(() => {
        // An undefined list is "not loaded yet", not "nothing open" — syncing it
        // as an empty list would clear every slot on the first render and make
        // the positions the user holds blink out until the query resolves.
        if (activeTrades === undefined) return;
        if (runtimeMode !== 'real') return;
        useTradingStore.getState()._syncActiveTrades(slots);
    }, [slots, activeTrades, runtimeMode]);
}
